import React, { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import { useAuthContext } from "../../context/AuthContext.tsx";
import { Link, useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import StoreIcon from "@mui/icons-material/Store";
import KeyIcon from "@mui/icons-material/Key";

interface Props {
  userData: any;
}

const SettingsMenu: React.FC<Props> = ({ userData }) => {
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const { setAuth } = useAuthContext();
  const navigate = useNavigate();

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const handleMenuClick = (text: string) => {
    if (text === "Logout") {
      const theme = localStorage.getItem('theme');
      localStorage.clear();
      if(theme) localStorage.setItem('theme', theme)
      setAuth(false);
      navigate("/");
    }
    setAnchorElUser(null);
  };

  const menuItems = [
    {
      label: "Edit Profile",
      description: "Manage personal information",
      route: "/profile",
      icon: <PermIdentityIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Change Password",
      description: "Update account credentials",
      route: "/change-pwd",
      icon: <KeyIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Manage Store",
      description: "Configure store access",
      route: "/manage-stores",
      icon: <StoreIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Logout",
      description: "End current session",
      route: "/",
      icon: <LogoutIcon sx={{ fontSize: 16 }} />,
      isLogout: true,
    },
  ];

  return (
    <>
      <Tooltip title="Open settings">
        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
          <Avatar
            alt={userData ? userData.firstName : ""}
            src={userData ? userData.profileImage : ""}
            sx={{ width: 36, height: 36 }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            elevation: 0,
            className: `
                  mt-3
                  min-w-[240px]
                  overflow-hidden
                  rounded-2xl
                  border
                  backdrop-blur-xl
                  shadow-2xl
                `,
            sx: {
              background: "rgb(var(--surface-1))",
              borderColor: "rgb(var(--border-primary))",
              boxShadow: "var(--shadow-card-hover)",
              backgroundImage: `
                    linear-gradient(
                      180deg,
                      rgba(var(--color-gold-1),0.06) 0%,
                      transparent 100%
                    )
                  `,
            },
          },
        }}
      >
        {/* Header */}
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-center gap-3">
            <Avatar
              alt={userData?.firstName || ""}
              src={userData?.profileImage || ""}
              sx={{
                width: 42,
                height: 42,
                border: "1px solid rgba(var(--color-gold-1),0.2)",
              }}
            />

            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-[rgb(var(--text-primary))] truncate">
                {userData
                  ? `${userData.firstName} ${userData.lastName || ""}`
                  : "Account"}
              </p>

              <p className="text-[11px] text-[rgb(var(--text-muted))] truncate">
                Account Settings
              </p>
            </div>
          </div>
        </div>

        <div className="mx-3 h-px bg-[rgb(var(--border-primary))]" />

        <div className="p-2">
          {menuItems.map((item) => (
            <Link
              key={item.label}
              to={item.route}
              className="block no-underline"
            >
              <MenuItem
                onClick={() => handleMenuClick(item.label)}
                className="
                      group
                      relative
                      overflow-hidden

                      !mb-1
                      !px-3
                      !py-2.5
                      !rounded-xl

                      flex
                      items-center
                      gap-3

                      transition-all
                      duration-200
                      ease-out

                      hover:translate-x-[1px]
                      active:scale-[0.99]
                    "
                sx={{
                  background: "transparent",

                  color: item.isLogout ? "#ef4444" : "rgb(var(--text-primary))",

                  "&:hover": {
                    background: item.isLogout
                      ? "rgba(239,68,68,0.06)"
                      : "rgb(var(--surface-2))",

                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  },
                }}
              >
                {/* Left Accent */}
                <div
                  className="
                        absolute
                        left-0
                        top-1/2
                        -translate-y-1/2

                        h-0
                        group-hover:h-5

                        w-[2px]
                        rounded-full

                        transition-all
                        duration-200
                      "
                  style={{
                    background: item.isLogout
                      ? "#ef4444"
                      : "rgb(var(--color-gold-1))",
                  }}
                />

                {/* Icon */}
                <div
                  className="
                        flex
                        items-center
                        justify-center

                        w-9
                        h-9
                        rounded-lg
                        shrink-0

                        transition-all
                        duration-200
                      "
                  style={{
                    background: item.isLogout
                      ? "rgba(239,68,68,0.08)"
                      : "rgb(var(--surface-3))",

                    color: item.isLogout
                      ? "#ef4444"
                      : "rgb(var(--text-secondary))",

                    border: "1px solid rgb(var(--border-primary))",
                  }}
                >
                  {item.icon}
                </div>

                {/* Label */}
                <div className="flex flex-col min-w-0">
                  <span
                    className="
                          text-[13px]
                          font-medium
                          leading-none
                        "
                  >
                    {item.label}
                  </span>

                  <span
                    className="
                          text-[11px]
                          mt-1
                          text-[rgb(var(--text-muted))]
                        "
                  >
                    {item.description}
                  </span>
                </div>

                {/* Arrow */}
                <div
                  className="
                        ml-auto
                        opacity-0
                        translate-x-1

                        group-hover:opacity-100
                        group-hover:translate-x-0

                        transition-all
                        duration-200
                      "
                  style={{
                    color: item.isLogout ? "#ef4444" : "rgb(var(--text-muted))",
                  }}
                >
                  →
                </div>
              </MenuItem>
            </Link>
          ))}
        </div>
      </Menu>
    </>
  );
};

export default SettingsMenu;
